/**
 * 资产页「手动资产 / 手动负债」区块（批 `107` · K1）
 *
 * 依据：`DR-030`（载体定稿 · **禁录投资理财类** · **不与 `major_assets` 双计**）·
 * J 域 PRD **§4.1.1 第 8 类** / **§4.1.2 第 7 类** · B 域 PRD §5.4.1（空态给下一步）·
 * 铁律 53（没入口 = 没兑现）· 铁律 121（**不 Portal ⇒ 可静态渲染**）。
 *
 * ## 🔴 它解决的是哪个用户问题
 *
 * 「借给朋友 5000 / 交了 2 万押金 / 有几瓶茅台」—— 这些钱**不在任何账户里**，
 * 也不该被记成一笔支出（支出会让当月开销虚高）。它们只能作为**手动项**存在。
 * 在此之前 `net_worth_items` 表是只读的 ⇒ 净资产**永远偏低**，而用户无从自证。
 *
 * ## 🔴 三条刻意不做的事
 *
 * | 不做 | 为什么 |
 * |---|---|
 * | **不做「类别」下拉（投资理财 / 房产…）** | 那会让"哪些类别不许选"变成第二处判断。本区块只有「资产 / 负债」两侧 + 一句文案指路 —— 投资理财在投资页，房产车辆在重大资产。`DR-030` 的原话就是「以录入时禁止替代事后剔除」 |
 * | **不自己算净资产、不把原币折本币** | 折算与合计只在 `composeNetWorth()` 里发生。此处只显示**原币**金额（`DR-004`：不折算、不跨币相加） |
 * | **不用 MUI `Dialog` 承载表单** | `Dialog` 走 Portal ⇒ `renderToStaticMarkup` 拿不到内容（铁律 121）。改用**内联折叠面板**，表单因此可被静态断言 |
 *
 * ## ⚠️ 「只提示不拦」的那一条
 *
 * 同名资产可能真的有两件（"茅台"有两瓶），所以**不做重名拦截**；
 * 页面上给一句「房产 / 车辆请记在『重大资产』，避免双计」的固定提示。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { NetWorthItemKind, type NetWorthItemView } from '@jiaying/shared';
import {
  amountErrorOf,
  draftErrorOf,
  draftFromItem,
  emptyItemDraft,
  nameErrorOf,
  type NetWorthItemDraft,
} from '../features/net-worth-item-draft.js';
import { Amount } from './Amount.js';

/** 空态给的三个例子 —— 这正是本区块要解决的用户问题（不是装饰） */
export const EMPTY_EXAMPLES = ['押金', '借出去的钱', '收藏品'] as const;

export interface NetWorthItemSectionProps {
  readonly items: readonly NetWorthItemView[];
  /** 账本本币（新建表单的默认币种 · 服务端下发） */
  readonly baseCurrency: string;
  /** 保存（`editingId === null` ⇒ 新增） */
  readonly onSubmit: (draft: NetWorthItemDraft, editingId: string | null) => void;
  readonly onDelete: (id: string) => void;
  readonly busy: boolean;
  /** 服务端错误原文（**端侧不编文案**） */
  readonly error: string | null;
}

export function NetWorthItemSection({
  items,
  baseCurrency,
  onSubmit,
  onDelete,
  busy,
  error,
}: NetWorthItemSectionProps): ReactElement {
  /** `null` = 表单收起；`''` = 新增中；其它 = 正在编辑的那条 id */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<NetWorthItemDraft>(emptyItemDraft(NetWorthItemKind.Asset, baseCurrency));
  /** 已点「删除」但还没确认的那条（内联二次确认，不用 Dialog） */
  const [confirmId, setConfirmId] = useState<string | null>(null);
  /** 本地校验错误（服务端错误走 `error` 属性 —— 两者分开，用户能分清是谁在报错） */
  const [localError, setLocalError] = useState<string | null>(null);

  const openNew = (kind: NetWorthItemKind): void => {
    setEditing('');
    setDraft(emptyItemDraft(kind, baseCurrency));
    setLocalError(null);
    setConfirmId(null);
  };

  const openEdit = (item: NetWorthItemView): void => {
    setEditing(item.id);
    setDraft(draftFromItem(item));
    setLocalError(null);
    setConfirmId(null);
  };

  const submit = (): void => {
    const err = draftErrorOf(draft);
    setLocalError(err);
    if (err !== null) return;
    onSubmit(draft, editing === '' ? null : editing);
    setEditing(null);
  };

  const assets = items.filter((i) => i.kind === NetWorthItemKind.Asset);
  const liabilities = items.filter((i) => i.kind === NetWorthItemKind.Liability);

  /**
   * 两侧共用的行渲染。
   *
   * 🔴 `showEmpty` 只在**整个区块一条都没有**时才为真 ——
   * 否则"已有资产、还没有负债"时，负债栏会冒出「还没有记录 —— 比如押金…」，
   * 而用户明明已经记了一条（他读到的意思是"我刚才那条没存上"）。
   */
  const rows = (list: readonly NetWorthItemView[], showEmpty: boolean): ReactElement => (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {list.length === 0 ? (
        showEmpty ? (
          <Typography variant="caption" color="text.secondary" data-testid="nwi-empty">
            还没有记录 —— 比如{EMPTY_EXAMPLES.join(' / ')}，记在这里会让净资产更接近真实。
          </Typography>
        ) : null
      ) : (
        list.map((item) => (
          <Stack
            key={item.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            data-testid={`nwi-row-${item.id}`}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.name}
                {/* 🔴 隐藏项不计入净资产 —— 必须说出来，否则用户以为总额算错了 */}
                {item.hidden && (
                  <Chip size="small" variant="outlined" label="已隐藏" sx={{ ml: 1 }} data-testid={`nwi-hidden-${item.id}`} />
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.ownerName}
                {item.note === null ? '' : ` · ${item.note}`}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Amount cents={item.amount} currency={item.currency} colored={false} variant="body2" />
              <Button size="small" data-testid={`nwi-edit-${item.id}`} onClick={() => { openEdit(item); }}>
                改
              </Button>
              {confirmId === item.id ? (
                <>
                  <Button
                    size="small"
                    color="error"
                    data-testid={`nwi-del-confirm-${item.id}`}
                    onClick={() => {
                      onDelete(item.id);
                      setConfirmId(null);
                    }}
                  >
                    确认删除
                  </Button>
                  <Button size="small" data-testid={`nwi-del-cancel-${item.id}`} onClick={() => { setConfirmId(null); }}>
                    取消
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  data-testid={`nwi-del-${item.id}`}
                  onClick={() => { setConfirmId(item.id); }}
                >
                  删除
                </Button>
              )}
            </Stack>
          </Stack>
        ))
      )}
    </Stack>
  );

  return (
    <Card data-testid="nwi-section" data-anchor="手动资产与负债">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          手动资产 / 手动负债
        </Typography>
        <Typography variant="caption" color="text.secondary">
          押金、借出去的钱、收藏品这类「不在账户里」的家底记在这里。
          <b>房产 / 车辆请记在「重大资产」</b>，投资理财请用「投资」页 —— 重复登记会双计。
        </Typography>

        {error !== null && (
          <Alert severity="error" sx={{ mt: 1.5 }} data-testid="nwi-error">
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} data-testid="nwi-head">
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, alignSelf: 'center' }}>
            资产（{assets.length}）
          </Typography>
          <Button size="small" variant="outlined" data-testid="nwi-add-asset" onClick={() => { openNew(NetWorthItemKind.Asset); }}>
            加资产
          </Button>
          <Button
            size="small"
            variant="outlined"
            data-testid="nwi-add-liability"
            onClick={() => { openNew(NetWorthItemKind.Liability); }}
          >
            加负债
          </Button>
        </Stack>
        {rows(assets, items.length === 0)}

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          负债（{liabilities.length}）
        </Typography>
        {rows(liabilities, items.length === 0)}

        {editing !== null && (
          <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="nwi-form">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {editing === '' ? '新增' : '编辑'}（{draft.kind === NetWorthItemKind.Asset ? '资产' : '负债'}）
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                label="名称"
                value={draft.name}
                onChange={(e) => { setDraft({ ...draft, name: e.target.value }); }}
                inputProps={{ 'data-testid': 'nwi-name' }}
                error={nameErrorOf(draft) !== null}
                helperText={nameErrorOf(draft) ?? ' '}
                fullWidth
              />
              <Stack direction="row" spacing={1.5}>
                <TextField
                  size="small"
                  label={`金额（${draft.currency}）`}
                  value={draft.amountText}
                  onChange={(e) => { setDraft({ ...draft, amountText: e.target.value }); }}
                  inputProps={{ 'data-testid': 'nwi-amount' }}
                  error={amountErrorOf(draft) !== null}
                  helperText={amountErrorOf(draft) ?? ' '}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="币种"
                  value={draft.currency}
                  onChange={(e) => { setDraft({ ...draft, currency: e.target.value.trim().toUpperCase() }); }}
                  inputProps={{ 'data-testid': 'nwi-currency', maxLength: 3 }}
                  sx={{ width: 100 }}
                />
              </Stack>
              <TextField
                size="small"
                label="备注（可不填）"
                value={draft.note}
                onChange={(e) => { setDraft({ ...draft, note: e.target.value }); }}
                inputProps={{ 'data-testid': 'nwi-note' }}
                fullWidth
              />
              {localError !== null && (
                <Alert severity="warning" data-testid="nwi-local-error">
                  {localError}
                </Alert>
              )}
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" disabled={busy} data-testid="nwi-save" onClick={submit}>
                  保存
                </Button>
                <Button size="small" data-testid="nwi-cancel" onClick={() => { setEditing(null); setLocalError(null); }}>
                  取消
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
